import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import GroupTable, { type GroupTableRow } from "../GroupTable";
import StripeTable from "../StripeTable";
import ResetBtn from "../ResetBtn";
import "./treatmentPlotSelection.less";

export interface TreatmentPlotSelectionProps {
  title: string;
  subtitle?: string;
  accentColorIndex: number | null;
  accentColor?: string;
  rows: GroupTableRow[];
  isStripes?: boolean;
  onShowPlot?: (id: string) => void;
  plotColumnLabel?: string;
  showActions?: boolean;
  onReset?: () => void;
  resetDisabled?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
  saveDisabled?: boolean;
}

const TreatmentPlotSelection: FC<TreatmentPlotSelectionProps> = ({
  title,
  subtitle,
  accentColorIndex,
  accentColor,
  rows,
  isStripes = false,
  onShowPlot,
  plotColumnLabel,
  showActions = true,
  onReset,
  resetDisabled,
  onCancel,
  onSave,
  saveDisabled = true,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="rd-treatment-plot-selection"
      data-testid="treatment-plot-selection"
    >
      <div className="rd-treatment-plot-selection__header">
        <div className="rd-treatment-plot-selection__titles">
          <div className="rd-treatment-plot-selection__heading">
            <span
              className={`rd-treatment-plot-selection__dot ${
                accentColorIndex != null
                  ? `rd-treatment-plot-selection__dot--${accentColorIndex}`
                  : ""
              }`}
              style={{ backgroundColor: accentColor }}
              aria-hidden="true"
            />
            <span className="rd-treatment-plot-selection__title">{title}</span>
          </div>
          {subtitle && (
            <span className="rd-treatment-plot-selection__subtitle">
              {subtitle}
            </span>
          )}
        </div>
        {showActions && (
          <ResetBtn
            disabled={resetDisabled ?? rows.length === 0}
            onClick={onReset}
          />
        )}
      </div>

      {rows.length > 0 &&
        (isStripes ? (
          <StripeTable
            rows={rows.map((row) => ({ id: row.id, stripe: row.plot }))}
            onShowStripe={onShowPlot}
            accentColorIndex={accentColorIndex}
            accentColor={accentColor}
          />
        ) : (
          <GroupTable
            rows={rows}
            onShowPlot={onShowPlot}
            showCheckbox={false}
            showSeedRate={false}
            showHeaderIcons={false}
            accentColorIndex={accentColorIndex}
            accentColor={accentColor}
            groupColumnUnit=""
            plotColumnLabel={plotColumnLabel}
            plotColumnUnit=""
          />
        ))}

      {showActions && (
        <div className="rd-treatment-plot-selection__actions">
          <button
            type="button"
            className="rd-treatment-plot-selection__cancel"
            onClick={onCancel}
          >
            {formatTranslation(t("newRatesAndDosage.cpBiological.cancel"))}
          </button>
          <button
            type="button"
            className="rd-treatment-plot-selection__save"
            disabled={saveDisabled}
            onClick={onSave}
          >
            {formatTranslation(t("newRatesAndDosage.cpBiological.save"))}
          </button>
        </div>
      )}
    </div>
  );
};

export default TreatmentPlotSelection;
